import { useCallback, useEffect, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';

// useAvatarChat — CHAT MODE.
//
// The world has two kinds of conversation, and they are different systems on
// purpose:
//
//   CHAT   (this)              — you walked up to an artist and started talking.
//                                Free-form, persistent, remembers you between
//                                sessions, and carries the whole ChatService
//                                pipeline: tools, guided-flow choice buttons,
//                                fan photos, reactions. AUDIO is not here — it
//                                belongs to the voice agent (see the note below
//                                `pick`), which speaks on this same thread.
//   SCENE  (useDialogueScene)  — an EVENT is happening. Directed, agenda-driven,
//                                stateless, and able to refuse to end while a
//                                track renders.
//
// The switch is the presence of a scene: an active scene takes the surface,
// everything else is chat. Same rule everywhere, so an avatar you were chatting
// with can be pulled into a scripted beat and handed back afterwards without
// either system knowing about the other.
//
// Deliberately the SAME RETURN SHAPE as useDialogueScene, so a conversation view
// can render either without caring which brain is behind it.
export function useAvatarChat({ avatarId, enabled = true }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [note, setNote] = useState('');

  const authed = { Authorization: `Bearer ${AUTH_TOKEN}`, 'Content-Type': 'application/json' };

  // History is the CANONICAL state, not a nicety. POST /api/chat/message returns
  // only the plain text reply — structured metadata (choice buttons, photo
  // attachments, reactions) lives on the persisted row, so anything richer than
  // text arrives on the re-read. Same reason the website refetches after a send.
  const loadHistory = useCallback(async () => {
    if (!avatarId) return [];
    try {
      const res = await fetch(`${API_BASE}/api/chat/history/${avatarId}?limit=60`, { headers: authed });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `history ${res.status}`);
      const list = data.messages || [];
      setMessages(list);
      return list;
    } catch (err) {
      setNote(err.message);
      return [];
    }
  }, [avatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !avatarId) return undefined;
    let dead = false;
    setOpening(true);
    setMessages([]);
    loadHistory().finally(() => { if (!dead) setOpening(false); });
    return () => { dead = true; };
  }, [avatarId, enabled, loadHistory]);

  // Choice buttons come off the LAST assistant message, and only while it is
  // still unanswered. `choice_response` is the server's record that this set was
  // already taken — without checking it, a re-read after any later message would
  // resurrect buttons the player already tapped.
  const last = messages.length ? messages[messages.length - 1] : null;
  const choices = (last?.role === 'assistant' && !last?.metadata?.choice_response)
    ? (last?.metadata?.choices || [])
    : [];

  const send = useCallback(async (text) => {
    const body = String(text ?? input).trim();
    if (!body || sending || !avatarId) return;
    setMessages((m) => [...m, { role: 'user', content: body, pendingLocal: true }]);
    setInput(''); setSending(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/chat/message`, {
        method: 'POST', headers: authed,
        body: JSON.stringify({ avatarId, message: body }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.requiresUpgrade) {
        setNote(data.reason || "That's all the free messages for now.");
      } else if (!data.success || !data.reply) {
        setNote('No reply came back — try again.');
      }
      await loadHistory();
    } catch (err) {
      setNote(err.message);
    } finally {
      setSending(false);
    }
  }, [avatarId, input, sending, loadHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tapping a choice is NOT the same as typing its value: the server has to mark
  // the set as taken atomically, or a double-tap sends the answer twice. It
  // routes the value through the same pipeline afterwards.
  const pick = useCallback(async (value) => {
    if (!last?.id || sending) return;
    setSending(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/chat/choice`, {
        method: 'POST', headers: authed,
        body: JSON.stringify({ messageId: last.id, value }),
      });
      // 409 = a stale tap (already answered, or an older message). The user's
      // most likely cause is a double-click, so it is not worth a message.
      if (!res.ok && res.status !== 409) {
        const data = await res.json().catch(() => ({}));
        setNote(data.error || `choice ${res.status}`);
      }
      await loadHistory();
    } catch (err) {
      setNote(err.message);
    } finally {
      setSending(false);
    }
  }, [last?.id, sending, loadHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // NO TTS HERE — ON PURPOSE.
  //
  // Speaking a reply by generating the whole text, then posting it to
  // /api/chat/tts, then waiting for a finished clip is two serial round-trips
  // before a single word is heard. Audio belongs to the VOICE AGENT
  // (useAvatarVoiceChat): the same ElevenLabs conversational agent the web app
  // uses, on the same thread, streaming its answer as it forms. This hook owns
  // the TEXT half of chat mode; the agent owns the spoken half, and a surface
  // mounts both.

  return {
    mode: 'chat',
    messages, input, setInput, send, sending, opening, choices, pick,
    closed: false, note, setNote, pending: null, track: null,
    reload: loadHistory,
  };
}

export default useAvatarChat;
